// [NEW FEATURE 6] CommandPalette — global ⌘K search modal.
//
// Searches contacts + LinkedIn threads + recent emails by name/email.
// Results are clickable links to the relevant detail page. Open via ⌘K
// (or Ctrl-K) — App.jsx wires the global keyboard listener.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection, query, where, orderBy, limit as fsLimit, getDocs,
} from 'firebase/firestore';
import { db } from '../firebase.js';

function debounce(fn, ms) {
  let t = null;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

async function searchContacts(qstr, uid) {
  if (!qstr || !uid) return [];
  // Firestore doesn't support contains-substring queries on strings — we
  // pull the latest 100 owned contacts and filter client-side. Good enough
  // for a single-operator product where contact counts stay <100k.
  try {
    const snap = await getDocs(query(
      collection(db, 'contacts'),
      where('owner_uid', '==', uid),
      orderBy('updated_at', 'desc'),
      fsLimit(200),
    ));
    const lower = qstr.toLowerCase();
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((c) => {
        const hay = `${c.name || ''} ${c.email || ''} ${c.company || ''} ${c.title || ''}`.toLowerCase();
        return hay.includes(lower);
      })
      .slice(0, 8)
      .map((c) => ({
        kind: 'contact',
        id: c.id,
        primary: c.name || c.email || c.id,
        secondary: [c.title, c.company].filter(Boolean).join(' · ') || c.email,
        href: `/contacts/${c.id}`,
      }));
  } catch (e) {
    console.warn('[palette] contacts search failed', e?.message);
    return [];
  }
}

async function searchThreads(qstr, uid) {
  if (!qstr || !uid) return [];
  try {
    const snap = await getDocs(query(
      collection(db, 'linkedin_threads'),
      where('owner_uid', '==', uid),
      orderBy('created_at', 'desc'),
      fsLimit(100),
    ));
    const lower = qstr.toLowerCase();
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((t) => {
        const hay = `${t.contact_name || ''} ${t.contact_handle || ''} ${t.preview || ''}`.toLowerCase();
        return hay.includes(lower);
      })
      .slice(0, 5)
      .map((t) => ({
        kind: 'thread',
        id: t.id,
        primary: t.contact_name || t.contact_handle || t.id,
        secondary: (t.preview || '').slice(0, 80),
        href: `/linkedin`,
      }));
  } catch (e) {
    console.warn('[palette] threads search failed', e?.message);
    return [];
  }
}

async function searchEmails(qstr, uid) {
  if (!qstr || !uid) return [];
  try {
    const snap = await getDocs(query(
      collection(db, 'emails'),
      where('owner_uid', '==', uid),
      orderBy('created_at', 'desc'),
      fsLimit(80),
    ));
    const lower = qstr.toLowerCase();
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((e) => {
        const hay = `${e.subject || ''} ${e.recipient_email || ''} ${e.recipient || ''}`.toLowerCase();
        return hay.includes(lower);
      })
      .slice(0, 5)
      .map((e) => ({
        kind: 'email',
        id: e.id,
        primary: e.subject || '(no subject)',
        secondary: e.recipient_email || e.recipient || '',
        href: `/emails/${e.id}`,
      }));
  } catch (e) {
    console.warn('[palette] emails search failed', e?.message);
    return [];
  }
}

export function CommandPalette({ open, onClose, uid }) {
  const [qstr, setQstr] = useState('');
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQstr(''); setResults([]); setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Debounced search.
  const search = useMemo(() => debounce(async (text, owner) => {
    if (!text.trim()) { setResults([]); return; }
    const [c, t, e] = await Promise.all([
      searchContacts(text, owner),
      searchThreads(text, owner),
      searchEmails(text, owner),
    ]);
    setResults([...c, ...t, ...e]);
    setActive(0);
  }, 180), []);

  useEffect(() => { search(qstr, uid); }, [qstr, uid, search]);

  if (!open) return null;

  const onKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(results.length - 1, a + 1)); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); return; }
    if (e.key === 'Enter') {
      const r = results[active];
      if (r) {
        e.preventDefault();
        // Navigate via location — simpler than router refs across portal.
        window.location.hash = '';
        window.history.pushState({}, '', r.href);
        window.dispatchEvent(new PopStateEvent('popstate'));
        onClose?.();
      }
    }
  };

  return (
    <div role="dialog" aria-label="Command palette"
         onClick={onClose}
         style={{
           position: 'fixed', inset: 0, zIndex: 60,
           background: 'rgba(0,0,0,0.45)',
           display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
           paddingTop: 80,
         }}>
      <div onClick={(e) => e.stopPropagation()}
           onKeyDown={onKey}
           style={{
             width: 'min(640px, 90vw)',
             background: 'var(--card)',
             border: '1px solid var(--rule)',
             boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
           }}>
        <input
          ref={inputRef}
          value={qstr}
          onChange={(e) => setQstr(e.target.value)}
          placeholder="Search contacts, threads, emails…"
          className="w-full px-3 py-3 text-[14px]"
          style={{
            background: 'transparent',
            color: 'var(--ink)',
            border: 'none',
            borderBottom: '1px solid var(--rule)',
            outline: 'none',
          }}
        />
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {!qstr.trim() ? (
            <div className="p-4 text-[11px]" style={{ color: 'var(--muted)' }}>
              Type to search. ↑/↓ to move · Enter to open · Esc to close.
            </div>
          ) : !results.length ? (
            <div className="p-4 text-[11px]" style={{ color: 'var(--muted)' }}>No matches.</div>
          ) : (
            results.map((r, i) => (
              <Link key={`${r.kind}-${r.id}`} to={r.href} onClick={onClose}
                    className="block px-3 py-2"
                    style={{
                      borderTop: '1px solid var(--rule)',
                      background: i === active ? 'var(--sunken, var(--btn-bg))' : 'transparent',
                      color: 'var(--ink)',
                    }}>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] uppercase tracking-[0.18em]" style={{ color: 'var(--muted)', minWidth: 60 }}>
                    {r.kind}
                  </span>
                  <span className="font-medium">{r.primary}</span>
                </div>
                {r.secondary && (
                  <div className="text-[10px] pl-[68px]" style={{ color: 'var(--muted)' }}>{r.secondary}</div>
                )}
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
